import { makeAutoObservable, runInAction } from 'mobx'
import {
  OptionsType,
  OptionsValue,
  TypesOption,
} from '~/shared/api/services/types'
import { LoadersOptions } from './types'
import { OptionsService } from '~/shared/api/services/options'
import { handleAxiosError } from '~/shared/lib'
import { mapToFilteredLocalization } from '~/widgets/options/lib/helpers/map-to-filtered-localization'
import { StoreLanguagesType } from '~/shared/api/services/stores/types'
import { ImageModel } from '~/shared/api/services/global/types'

const DEFAULT_FORM_DATA = {
  type: null,
  localizations: {},
  name: '',
  values: [
    {
      name: '',
      image: null,
      imageId: null,
      sizeUs: null,
      sizeInternational: null,
      sizeRu: null,
      sizeInches: null,
      sizeCentimeters: null,
      sortOrder: '',
      localizations: {},
    },
  ],
}

export class OptionsStores {
  loaders: LoadersOptions = {
    options: false,
    createNewOption: false,
    updateOption: false,
    optionById: false,
    loadMoreOptions: false,
  }

  formData: OptionsType = DEFAULT_FORM_DATA

  valuesWithType: Record<string, OptionsValue[]> = {}

  requestSettings = {
    limit: 20,
    offset: 0,
    isAllLoaded: false,
  }

  options: OptionsType[] = []

  constructor() {
    makeAutoObservable(this)
  }

  activeLoader = (key: keyof LoadersOptions) => {
    this.loaders = { ...this.loaders, [key]: true }
  }

  closeLoader = (key: keyof LoadersOptions) => {
    this.loaders = { ...this.loaders, [key]: false }
  }

  setFormData = <K extends keyof OptionsType>(
    key: K,
    value: OptionsType[K],
  ) => {
    this.formData = { ...this.formData, [key]: value }
  }

  setTypeWithValues = (prevType: string | null, curType: string) => {
    if (prevType) {
      this.valuesWithType[prevType] = [...this.formData.values]
    }

    this.formData.type = curType as TypesOption

    const savedValues = this.valuesWithType[curType]

    this.formData = {
      ...this.formData,
      values: savedValues ? [...savedValues] : [...DEFAULT_FORM_DATA.values],
    }
  }

  setFormLocalizationData = (key: string, value: string) => {
    this.formData = {
      ...this.formData,
      localizations: {
        ...this.formData.localizations,
        [key]: {
          name: value,
        },
      },
    }
  }

  setFormDataLocalizationValue = (
    index: number,
    locale: string,
    value: string,
  ) => {
    this.formData = {
      ...this.formData,
      values: this.formData.values.map((item, i) =>
        i === index
          ? {
              ...item,
              localizations: {
                ...item.localizations,
                [locale]: {
                  ...item.localizations?.[locale],
                  name: value,
                },
              },
            }
          : item,
      ),
    }
  }

  setFormDataValue = (index: number, key: string, value: string) => {
    this.formData = {
      ...this.formData,
      values: this.formData.values.map((item, i) =>
        i === index
          ? {
              ...item,
              [key]: value,
            }
          : item,
      ),
    }
  }

  setFormDataImageValue = (index: number, image: ImageModel | null) => {
    this.formData = {
      ...this.formData,
      values: this.formData.values.map((item, i) =>
        i === index
          ? {
              ...item,
              image: image?.url || '',
              imageId: image?.id,
            }
          : item,
      ),
    }
  }

  setFormDataImageDelete = (index: number) => {
    this.formData = {
      ...this.formData,
      values: this.formData.values.map((item, i) =>
        i === index
          ? {
              ...item,
              image: null,
            }
          : item,
      ),
    }
  }

  addValue = (languages: StoreLanguagesType[]) => {
    const emptyLocalizations = {}

    const localizations =
      mapToFilteredLocalization(emptyLocalizations, languages) || {}

    const newValue = {
      name: '',
      image: null,
      imageId: null,
      sortOrder: '',
      sizeUs: null,
      sizeInternational: null,
      sizeRu: null,
      sizeInches: null,
      sizeCentimeters: null,
      localizations,
    }

    this.formData = {
      ...this.formData,
      values: [...this.formData.values, newValue],
    }
  }

  clearFormData = () => {
    this.formData = {
      ...DEFAULT_FORM_DATA,
      values: [...DEFAULT_FORM_DATA.values],
    }
    this.valuesWithType = {}
  }

  getOptions = async (action?: 'loadMoreOptions') => {
    if (action === 'loadMoreOptions' && this.requestSettings.isAllLoaded) return
    this.activeLoader(action || 'options')

    try {
      const params = {
        limit: this.requestSettings.limit,
        offset: action !== 'loadMoreOptions' ? 0 : this.requestSettings.offset,
      }

      const { data } = await OptionsService.getOptions({ params })

      if (data.length !== 0) {
        if (action === 'loadMoreOptions') {
          runInAction(() => {
            this.options = [...this.options, ...data]
          })
        } else {
          runInAction(() => {
            this.options = data
          })
        }
        this.requestSettings = {
          ...this.requestSettings,
          isAllLoaded: data.length < this.requestSettings.limit,
          offset: action
            ? this.requestSettings.offset + this.requestSettings.limit
            : 20,
        }
      }
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      this.closeLoader(action || 'options')
    }
  }

  getOptionById = async (id: string) => {
    try {
      this.activeLoader('optionById')
      const { data } = await OptionsService.getOptionById(id)

      runInAction(() => {
        this.formData = data
        if (data.type) {
          this.valuesWithType[data.type] = [...data.values]
        }
      })
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      this.closeLoader('optionById')
    }
  }
}
